import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { speak, stopSpeech } from "../utils/speechUtils";
import {
  Send,
  Mic,
  MicOff,
  Bot,
  User,
  Sparkles,
  AlertTriangle,
  Cpu,
  Loader2,
  FilePlus,
  RotateCcw,
  ChevronDown,
  Volume2,
  VolumeX,
} from "lucide-react";
import { assistantAPI, machinesAPI } from "../api";
import { useAuthStore } from "../store/authStore"; // Import auth store for role
import clsx from "clsx";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import HasPermission from "../components/HasPermission";
import { PERMISSIONS } from "../constants/roles";

const MessageBubble = ({ message, isUser, onSpeak }) => {
  const [isSpeaking, setIsSpeaking] = useState(false);

  // FIX: Properly toggle speaker off if already speaking
  const handleSpeak = () => {
    if (isSpeaking) {
      stopSpeech(); // Using the utility
      setIsSpeaking(false);
    } else {
      setIsSpeaking(true);
      // You can now easily pass tonality options here
      speak(
        message.content,
        {
          rate: 0.9, // Slightly slower for better clarity in factories
          pitch: 1.1, // Slightly higher for a "helpful" tone
          voiceName: "Microsoft Zira", // Example for a specific Windows voice
        },
        () => setIsSpeaking(false),
      );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={clsx(
        "flex gap-3 max-w-4xl",
        isUser ? "ml-auto flex-row-reverse" : "",
      )}
    >
      <div
        className={clsx(
          "w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0",
          isUser
            ? "bg-blue-500"
            : "bg-gradient-to-br from-purple-500 to-pink-500",
        )}
      >
        {isUser ? (
          <User className="w-4 h-4 text-white" />
        ) : (
          <Bot className="w-4 h-4 text-white" />
        )}
      </div>
      <div
        className={clsx(
          "rounded-2xl px-4 py-3 max-w-[80%] relative group",
          isUser ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-800",
        )}
      >
        <p className="whitespace-pre-wrap">{message.content}</p>

        {!isUser && (
          <button
            onClick={handleSpeak}
            className="absolute -right-10 top-2 p-2 text-slate-400 hover:text-purple-500 opacity-0 group-hover:opacity-100 transition-all"
            title={isSpeaking ? "Stop" : "Read aloud"}
          >
            {isSpeaking ? (
              <VolumeX className="w-4 h-4 animate-pulse text-purple-600" />
            ) : (
              <Volume2 className="w-4 h-4" />
            )}
          </button>
        )}

        {message.sources && message.sources.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-200">
            <p className="text-xs text-slate-500 mb-2">Sources:</p>
            <div className="flex flex-wrap gap-2">
              {message.sources.map((source, i) => (
                <span
                  key={i}
                  className="text-xs bg-slate-200 px-2 py-1 rounded"
                >
                  {source.title}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const SuggestionChip = ({ text, onClick }) => (
  <button
    onClick={() => onClick(text)}
    className="px-4 py-2 bg-white hover:bg-slate-50 border border-slate-200 
               rounded-xl text-sm text-slate-600 hover:text-slate-800 transition-all"
  >
    {text}
  </button>
);

const VoiceVisualizer = ({ isActive, volume }) => {
  return (
    <div className="flex items-center gap-0.5 px-2 h-4">
      {[1, 2, 3, 4, 5].map((bar) => (
        <motion.div
          key={bar}
          animate={{
            height: isActive
              ? Math.max(4, (volume / 255) * 40 * (bar * 0.2))
              : 4,
          }}
          className="w-1 bg-red-500 rounded-full transition-all duration-75"
        />
      ))}
    </div>
  );
};

const Assistant = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const machineId = searchParams.get("machineId");
  const { user } = useAuthStore(); // Fetch user role

  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [micVolume, setMicVolume] = useState(0);
  const [selectedMachine, setSelectedMachine] = useState(machineId || "");
  const [isExtracting, setIsExtracting] = useState(false);
  const [isMachineDropdownOpen, setIsMachineDropdownOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const machineDropdownRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const streamRef = useRef(null);

  const isListeningRef = useRef(false);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition && !recognitionRef.current) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = "en-IN";

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = "";
        let interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript || interimTranscript) {
          setInput(finalTranscript || interimTranscript);
        }
      };

      recognitionRef.current.onend = () => {
        if (isListeningRef.current) {
          try {
            recognitionRef.current.start();
          } catch (e) {
            console.warn("Recognition restart failed:", e);
          }
        }
      };

      recognitionRef.current.onerror = (event) => {
        if (
          event.error === "not-allowed" ||
          event.error === "service-not-allowed"
        ) {
          toast.error("Microphone access denied.");
          setIsListening(false);
          isListeningRef.current = false;
        }
      };
    }

    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      stopAudioVisualizer();
      window.speechSynthesis.cancel();
    };
  }, []);

  const startAudioVisualizer = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioContextRef.current = new (
        window.AudioContext || window.webkitAudioContext
      )();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      const updateVolume = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setMicVolume(average);
        animationFrameRef.current = requestAnimationFrame(updateVolume);
      };
      updateVolume();
    } catch (err) {
      toast.error("Microphone hardware error.");
    }
  };

  const stopAudioVisualizer = () => {
    if (animationFrameRef.current)
      cancelAnimationFrame(animationFrameRef.current);
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    analyserRef.current = null;
    audioContextRef.current = null;
    streamRef.current = null;
    setMicVolume(0);
  };

  const toggleListening = async () => {
    if (isListening) {
      isListeningRef.current = false;
      recognitionRef.current?.stop();
      stopAudioVisualizer();
      setIsListening(false);
    } else {
      setInput("");
      isListeningRef.current = true;
      await startAudioVisualizer();
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };

  const speakText = (text, onEnd) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.onend = onEnd;
    window.speechSynthesis.speak(utterance);
  };

  const { data: machines } = useQuery({
    queryKey: ["machines-list"],
    queryFn: () =>
      machinesAPI.getAll({ limit: 100 }).then((res) => res.data.data),
  });

  const { data: suggestions } = useQuery({
    queryKey: ["suggestions", selectedMachine],
    queryFn: () =>
      selectedMachine
        ? assistantAPI
            .getSuggestions(selectedMachine)
            .then((res) => res.data.data)
        : Promise.resolve([]),
    enabled: !!selectedMachine,
  });

  const chatMutation = useMutation({
    mutationFn: (data) => assistantAPI.chat(data),
    onSuccess: (response) => {
      const data = response.data.data;
      setSessionId(data.sessionId);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.response,
          sources: data.sources,
          intent: data.intent,
          timestamp: new Date(),
        },
      ]);
    },
    onError: (error) => {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again.",
          isError: true,
          timestamp: new Date(),
        },
      ]);
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (machineId) setSelectedMachine(machineId);
  }, [machineId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        machineDropdownRef.current &&
        !machineDropdownRef.current.contains(event.target)
      ) {
        setIsMachineDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSend = () => {
    if (!input.trim() || chatMutation.isPending) return;

    const userMessage = {
      role: "user",
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    // MODIFIED: Added user role to the chat mutation payload
    chatMutation.mutate({
      message: input.trim(),
      machineId: selectedMachine || undefined,
      sessionId,
      userRole: user?.role, // Sending the role for context-aware AI responses
    });

    setInput("");
    inputRef.current?.focus();
  };

  const handleExtractSOP = async () => {
    if (messages.length < 2) {
      toast.error("Not enough context.");
      return;
    }
    setIsExtracting(true);
    try {
      const response = await assistantAPI.extractSop({ chatHistory: messages });
      const suggestedSop = response.data.data;
      if (suggestedSop) {
        if (selectedMachine) suggestedSop.machineId = selectedMachine;
        navigate("/sops/create", { state: { initialData: suggestedSop } });
      }
    } catch (error) {
      toast.error("Failed to extract SOP.");
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setInput("");
    window.speechSynthesis.cancel();
  };

  const defaultSuggestions = [
    "How do I operate this machine safely?",
    "What are the daily startup procedures?",
    "How do I troubleshoot common errors?",
    "Show me the maintenance schedule",
  ];

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col bg-white rounded-2xl p-4 border border-slate-200">
      <div className="flex items-center justify-between pb-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">AI Assistant</h1>
            <p className="text-sm text-slate-500">
              Ask me anything about your machines
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {messages.length > 1 && (
            <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
              <button
                onClick={handleExtractSOP}
                disabled={isExtracting}
                className="btn btn-secondary btn-sm gap-2 border-purple-200 bg-purple-50 hover:bg-purple-100"
              >
                {isExtracting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FilePlus className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Create SOP</span>
              </button>
            </HasPermission>
          )}

          <div ref={machineDropdownRef} className="relative">
            <button
              onClick={() => setIsMachineDropdownOpen((prev) => !prev)}
              className="btn btn-secondary w-full sm:w-auto justify-between h-10 min-w-[150px]"
            >
              <span className="truncate">
                {selectedMachine
                  ? machines?.find((m) => m.id === selectedMachine)?.name
                  : "All Machines"}
              </span>
              <ChevronDown
                className={clsx(
                  "w-4 h-4 transition-transform",
                  isMachineDropdownOpen && "rotate-180",
                )}
              />
            </button>
            <AnimatePresence>
              {isMachineDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-64 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-10 max-h-60 overflow-y-auto"
                >
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setSelectedMachine("");
                        setIsMachineDropdownOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      All Machines
                    </button>
                    {machines?.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => {
                          setSelectedMachine(m.id);
                          setIsMachineDropdownOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        {m.name}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={handleNewChat}
            className="btn btn-ghost"
            title="New conversation"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-6 space-y-6">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mb-6">
              <Bot className="w-10 h-10 text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              How can I help you today?
            </h2>
            <div className="flex flex-wrap gap-3 justify-center max-w-2xl">
              {(suggestions || defaultSuggestions).map((suggestion, i) => (
                <SuggestionChip
                  key={i}
                  text={suggestion}
                  onClick={handleSuggestionClick}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageBubble
                key={index}
                message={message}
                isUser={message.role === "user"}
                onSpeak={speakText}
              />
            ))}
            {chatMutation.isPending && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-slate-100 rounded-2xl px-4 py-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-slate-500">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <div className="pt-4 border-t border-slate-200">
        <div className="flex items-center gap-3">
          {" "}
          {/* Changed items-end to items-center */}
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                selectedMachine ? `Ask about machine...` : "Ask me anything..."
              }
              className="input min-h-[52px] max-h-32 resize-none pr-32"
              rows={1}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {" "}
              {/* Centering Fix */}
              <VoiceVisualizer isActive={isListening} volume={micVolume} />
              <button
                type="button"
                onClick={toggleListening}
                className={clsx(
                  "p-2 rounded-lg transition-all",
                  isListening
                    ? "bg-red-500 text-white animate-pulse"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-100",
                )}
                title="Voice input"
              >
                {isListening ? (
                  <MicOff className="w-4 h-4" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={handleSend}
                disabled={!input.trim() || chatMutation.isPending}
                className="btn btn-primary p-2"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-2 text-center">
          ConvoLink AI provides guidance based on documentation. Verify critical
          operations.
        </p>
      </div>
    </div>
  );
};

export default Assistant;
